import React, { useMemo } from 'react';
import { useCashListTransactionsPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import type {
  CashManagementTransactionType,
  GetCashManagementTransactionDto,
} from '@pflegenavi/shared/api';

export interface WithCashListTransactionsProps {
  cashListId: string;
  dateFrom: Date;
  dateTo: Date;
}

export interface CashListTransactionsData {
  data: GetCashManagementTransactionDto[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Higher-order component that provides cash list transactions data
 * using the Phoenix endpoint
 */
export function withCashListTransactions<
  P extends WithCashListTransactionsProps
>(
  Component: React.ComponentType<P & CashListTransactionsData>
): React.ComponentType<P> {
  return function WithCashListTransactionsComponent(props: P) {
    // Use Phoenix endpoint
    const phoenixQuery = useCashListTransactionsPhoenix(
      {
        cash_list_id: props.cashListId,
        page: 1,
        page_size: 1000, // Get all transactions for the period
        date_from: props.dateFrom,
        date_to: props.dateTo,
      },
      {
        enabled: !!props.cashListId,
      }
    );

    // Convert Phoenix data to expected format
    const data = useMemo(() => {
      if (!phoenixQuery.data) {
        return undefined;
      }

      // Check if we've hit the pagination limit
      if (phoenixQuery.data.meta.total_count > 1000) {
        throw new Error(
          `Transaction count (${phoenixQuery.data.meta.total_count}) exceeds maximum supported limit of 1000. ` +
            `Please narrow your date range. This is a known limitation that mimics legacy behavior.`
        );
      }

      // Convert Phoenix response to expected format
      return phoenixQuery.data.data.map((transaction) => ({
        id: transaction.id,
        cashListId: transaction.cash_list_id,
        employeeId: transaction.employee_id,
        date: new Date(transaction.inserted_at),
        type: transaction.type as unknown as CashManagementTransactionType,
        amountInCents: transaction.amount,
        amount: transaction.amount / 100,
        separateFeeAmountInCents: transaction.separate_fee_amount,
        immediateFeeAmountInCents: transaction.immediate_fee_amount,
        notes: transaction.notes || '',
        coins: (transaction.coin_change ?? []).map(
          (coin: { factor: number; amount: number }) => ({
            factor: coin.factor,
            amount: coin.amount,
          })
        ),
        bankAccountAmount: transaction.bank_account_amount_change,
        groupId: transaction.group_id,
        cashListTransactionGroupId: transaction.group_id,
        old_balance: transaction.balance_before,
        new_balance: transaction.balance_after,
        balanceBeforeInCents: transaction.balance_before,
        balanceAfterInCents: transaction.balance_after,
        relatedResidents: transaction.related_residents.map((resident) => ({
          id: resident.id,
          firstName: resident.firstname,
          lastName: resident.lastname,
        })),
        reversal: transaction.full_reversal,
        resident_id: transaction.related_residents?.[0]?.id,
        pliantTransactionId: transaction.pliant_transaction_id,
        employee: transaction.employee
          ? {
              firstName: transaction.employee.first_name,
              lastName: transaction.employee.last_name,
              id: transaction.employee.id,
            }
          : {
              firstName: '',
              lastName: '',
              id: '',
            },
        coinChange: transaction.coin_change,
        cash_list_id: transaction.cash_list_id,
        eventDate: new Date(transaction.event_datetime),
        status: 'confirmed' as const,
        balanceAfter: transaction.balance_after / 100,
        balanceBefore: transaction.balance_before / 100,
        transactionId: transaction.id,
      })) satisfies GetCashManagementTransactionDto[];
    }, [phoenixQuery.data]);

    return (
      <Component
        {...props}
        data={data}
        isLoading={phoenixQuery.isLoading}
        isError={phoenixQuery.isError}
      />
    );
  };
}
